/**
 * The two states every student screen can land in (build spec 4.4, spec 9.6): the cold-start
 * splash that tolerates ADR 0012's sleeping free instance, and the offline screen.
 */

import { useEffect, useState } from "react";
import { Button } from "../shared/primitives/Button.js";

/**
 * ADR 0012: the first request of the morning wakes a sleeping Render instance, which takes about
 * a minute.
 * One line after 4 s covers the usual case; the second exists because a screen that has not
 * changed in twenty seconds reads as broken to a ten-year-old, who then reloads and starts over.
 */
const SLOW_AFTER_MS = 4000;
const STILL_WAITING_AFTER_MS = 20_000;

export function Splash() {
  const [waited, setWaited] = useState(0);
  useEffect(() => {
    const first = setTimeout(() => setWaited(1), SLOW_AFTER_MS);
    const second = setTimeout(() => setWaited(2), STILL_WAITING_AFTER_MS);
    return () => {
      clearTimeout(first);
      clearTimeout(second);
    };
  }, []);

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 bg-[var(--color-bg)] px-6 text-center">
      <div
        className="splash-pulse text-[72px] leading-none font-black text-[var(--color-accent)]"
        role="status"
        aria-label="Scrolivo lädt"
      >
        S
      </div>
      {waited > 0 && (
        <p className="m-0 text-[15px] text-white/60">
          {waited === 1 ? "Scrolivo wacht auf, einen Moment…" : "Gleich geht’s los, noch kurz…"}
        </p>
      )}
    </div>
  );
}

/**
 * The dead end with a way out. It is not only the network state: the feed also lands here when
 * the category has no topic to show, so the heading and the icon are overridable — telling a
 * child «Keine Verbindung» when the connection is fine and the shelf is simply empty sends
 * them to the wrong person for help.
 */
export function OfflineState({
  onRetry,
  message,
  title = "Keine Verbindung",
  icon = "📡",
}: {
  onRetry: () => void;
  message?: string;
  title?: string;
  icon?: string;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-5 bg-[var(--color-bg)] px-8 text-center">
      <div className="text-[64px] leading-none">{icon}</div>
      <h1 className="m-0 text-[26px] font-black text-white">{title}</h1>
      <p className="m-0 max-w-[280px] text-[15px] text-white/70">
        {message ?? "Scrolivo kann gerade nicht mit dem Internet sprechen."}
      </p>
      <Button
        onClick={onRetry}
        className="mt-2 rounded-full bg-[var(--color-accent)] px-11 py-4 text-[18px] font-extrabold text-black"
      >
        Nochmal versuchen
      </Button>
    </div>
  );
}
